import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import {
  Badge,
  Button,
  ComponentMultiStyleConfig,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { hasValue } from "../../../lib/util/common-util";

export type CMSelectableListItemWithBadgeProps = {
  /**
   * ラベル。
   */
  label: string;

  /**
   * この項目が選択状態ならtrue。
   */
  selected?: boolean;

  /**
   * バッジの文字。
   * この値がある場合だけ、バッジを表示する。
   */
  badgeText?: string;

  /**
   * バッジの色。
   */
  badgeColor?: string;

  /**
   * バッジのバリアント。
   */
  badgeVariant?: "subtle" | "solid" | "outline";

  onClick?: () => void;
} & ComponentStyleProps;

/**
 * 選択可能な、バッジ付きリスト項目を表示するコンポーネント。
 */
export const CMSelectableListItemWithBadge = ({
  label,
  selected = false,

  badgeText,
  badgeColor = "blue.500",
  badgeVariant = "solid",

  onClick,

  sx,
  ...rest
}: CMSelectableListItemWithBadgeProps) => {
  const styles = useMultiStyleConfig("CMSelectableListItemWithBadgeStyles");
  const containerStyles = useMemo(() => {
    return {
      ...styles.container,
      ...(selected ? styles.containerSelected : undefined),
      ...sx,
    };
  }, [selected, styles.container, styles.containerSelected, sx]);

  const handleClick = useCallback(() => {
    if (selected) return;
    onClick?.();
  }, [selected, onClick]);

  return (
    <Button
      variant="ghost"
      sx={containerStyles}
      {...rest}
      onClick={handleClick}
    >
      <Text sx={styles.label}>{label}</Text>
      {hasValue(badgeText) && (
        <Badge
          variant={badgeVariant}
          colorScheme={badgeColor}
          sx={styles.badge}
        >
          <Text sx={styles.badgeText}>{badgeText}</Text>
        </Badge>
      )}
    </Button>
  );
};

export const CMSelectableListItemWithBadgeStyles: ComponentMultiStyleConfig = {
  parts: ["container", "containerSelected", "label", "badge", "badgeText"],
  baseStyle: {
    container: {
      borderRadius: "md",
      px: "8px",
      py: "4px",
      fontWeight: "normal",
      // w: "unset",

      display: "flex",
      flexFlow: "row nowrap",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "8px",

      textTransform: "none",

      overflow: "hidden",
    },
    containerSelected: {
      bgColor: "blue.100",
    },
    label: {
      flex: "1 1 content",
      minW: 0,
      maxW: "max-content",
      overflow: "hidden",
      textOverflow: "ellipsis",
      // textAlign: "start",

      fontSize: "sm",
      fontcolor: "gray.700",
    },
    badge: {
      flex: "0 0 auto",
      ml: "auto",

      fontSize: "xs",
      h: "20px",
      paddingRight: "10px",
      paddingLeft: "10px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "md",

      textTransform: "none",
    },
  },
};
